import React, { useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { updateTrackingStep, uploadDeliveryImageB64 } from '../pages/Report/utils/api-action';

export default function OrderImgUploadModal({ open, onClose, onSuccess, serviceNo, stepContext, initialImg, initialNote, grabUrl }) {
    const [base64, setBase64] = useState(null);
    const [note, setNote] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setBase64(initialImg ?? null);
            setNote(initialNote ?? '');
            setIsDragging(false);
            setSaving(false);
            setErrorMsg('');
        }
    }, [open, initialImg, initialNote]);

    const readFile = (file) => {
        if (!file) return;
        if (!file.type.startsWith('image/')) { setErrorMsg('กรุณาเลือกไฟล์รูปภาพเท่านั้น'); return; }
        setErrorMsg('');
        const reader = new FileReader();
        reader.onload = () => {
            if (typeof reader.result === 'string') setBase64(reader.result.split(',')[1]);
        };
        reader.readAsDataURL(file);
    };

    // Ctrl+V paste จาก clipboard
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            const item = [...(e.clipboardData?.items ?? [])].find(i => i.type.startsWith('image/'));
            if (item) readFile(item.getAsFile());
        };
        window.addEventListener('paste', handler);
        return () => window.removeEventListener('paste', handler);
    }, [open]);

    const onDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        readFile(e.dataTransfer.files?.[0]);
    };

    const onSave = async () => {
        if (!base64) return;
        setSaving(true);
        setErrorMsg('');
        try {
            // 1. Upload รูปขึ้น S3 ก่อน แล้วรับ path กลับมา
            const uploadRes = await uploadDeliveryImageB64(base64);
            const imagePath = uploadRes?.data?.content;
            if (!imagePath) {
                setErrorMsg('อัปโหลดรูปไม่สำเร็จ กรุณาลองใหม่');
                return;
            }
            // 2. บันทึก path (ไม่ใช่ base64) ลง tracking
            const res = await updateTrackingStep({ order_no: serviceNo, status_code: stepContext, tracking_image: imagePath, note: note || null, grab_url: grabUrl || undefined });
            if (res?.data?.code === 200) {
                onSuccess?.(imagePath);
            } else {
                setErrorMsg('บันทึกไม่สำเร็จ กรุณาลองใหม่');
            }
        } catch {
            setErrorMsg('เกิดข้อผิดพลาด กรุณาลองใหม่');
        } finally {
            setSaving(false);
        }
    };

    const preview = base64 ? `data:image/jpeg;base64,${base64}` : null;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ m: 0, p: 2 }}>
                {stepContext === 'PUP' ? 'รูปตะกร้าผ้า — ไรเดอร์รับผ้า' : stepContext === 'DLVD' ? 'รูปตะกร้าผ้า — ไรเดอร์ส่งผ้า' : 'แนบรูปตะกร้าผ้า'}
                {serviceNo && (
                    <span style={{ fontSize: 13, fontWeight: 400, color: '#6b7280', marginLeft: 8 }}>
                        #{serviceNo}
                    </span>
                )}
                <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, color: 'grey.500' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {/* ── Drop zone ── */}
                <div
                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={onDrop}
                    onClick={() => !preview && fileInputRef.current?.click()}
                    style={{
                        border: `2px dashed ${isDragging ? '#303F9F' : '#d1d5db'}`,
                        borderRadius: 8,
                        padding: 24,
                        textAlign: 'center',
                        cursor: preview ? 'default' : 'pointer',
                        backgroundColor: isDragging ? 'rgba(48,63,159,0.06)' : '#fafafa',
                        minHeight: 160,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        marginBottom: 16,
                        transition: 'border-color 0.15s, background-color 0.15s',
                    }}
                >
                    {preview ? (
                        <>
                            <img
                                src={preview}
                                alt="preview"
                                style={{ maxHeight: 220, maxWidth: '100%', borderRadius: 6, objectFit: 'contain' }}
                            />
                            <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => { e.stopPropagation(); setBase64(null); }}
                                sx={{ position: 'absolute', top: 6, right: 6, backgroundColor: '#fff', boxShadow: 1 }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </>
                    ) : (
                        <div style={{ color: '#9ca3af', userSelect: 'none' }}>
                            <div style={{ fontSize: 40, marginBottom: 8 }}>🖼️</div>
                            <div style={{ fontSize: 14 }}>
                                ลากวางรูปที่นี่ หรือ{' '}
                                <span style={{ color: '#303F9F', textDecoration: 'underline', cursor: 'pointer' }}>
                                    เลือกไฟล์
                                </span>
                            </div>
                            <div style={{ fontSize: 12, marginTop: 6, color: '#b0b8c1' }}>
                                กด <kbd style={{ background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, padding: '1px 5px', fontSize: 11 }}>Ctrl</kbd>
                                {' + '}
                                <kbd style={{ background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, padding: '1px 5px', fontSize: 11 }}>V</kbd>
                                {' '}วางจาก clipboard ได้เลย
                            </div>
                        </div>
                    )}
                </div>

                <input
                    hidden
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    onClick={(e) => (e.target.value = null)}
                    onChange={(e) => readFile(e.target.files?.[0])}
                />

                {/* ── Note ── */}
                <TextField
                    label="หมายเหตุ"
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    placeholder="ระบุหมายเหตุ (ถ้ามี)"
                />

                {errorMsg && (
                    <div style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>{errorMsg}</div>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button onClick={onClose} variant="outlined" color="error" size="small" disabled={saving}>
                    ยกเลิก
                </Button>
                <Button
                    onClick={onSave}
                    variant="contained"
                    size="small"
                    disabled={!base64 || saving}
                >
                    {saving ? 'กำลังบันทึก...' : 'บันทึก'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
